import { forwardRef, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { Eye, EyeSlash } from "iconsax-react";
import { useHighlightFade } from "@/shared/hooks/useHighlightFade";
import styles from "./Input.module.css";

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  placeholder: string;
  error?: string;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  disabled?: boolean;
  isTextarea?: boolean;
  isSmall?: boolean;
  onlyNumber?: boolean;
  autoComplete?: string;
  hasRequiredLabel?: boolean;
  highlight?: boolean;
}

export const Input = forwardRef<HTMLInputElement | HTMLTextAreaElement, Props>(
  (
    {
      label,
      placeholder,
      error,
      icon,
      className = "",
      children,
      disabled = false,
      isTextarea = false,
      isSmall = false,
      onlyNumber = false,
      autoComplete,
      hasRequiredLabel = false,
      highlight = false,
      id,
      type,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation("Common");
    const generatedId = useId();
    const inputId = id || generatedId;
    const isPasswordField = type === "password";
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const renderedType = isPasswordField && isPasswordVisible ? "text" : type;
    const showHighlight = useHighlightFade(highlight) && !error;
    const highlightClass = showHighlight ? "input-highlight-box-shadow" : "";

    const autoCompleteValue = onlyNumber ? "off" : autoComplete !== undefined ? autoComplete : "off";

    function handleNumberInput(e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) {
      if (!onlyNumber) return;

      const allowed = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End"];
      const key = e.key;

      if (allowed.includes(key)) return;
      if (/^\d$/.test(key)) return;

      const value = (e.target as HTMLInputElement).value;
      if (key === "." && !value.includes(".")) return;

      e.preventDefault();
    }

    return (
      <div className={cn("flex flex-col gap-2 w-full", isTextarea && "h-full")}>
        {label && (
          <div className="flex items-center gap-1">
            <label
              htmlFor={inputId}
              className={cn(
                "text-label-sm",
                disabled ? "content-base-low" : "content-base-primary",
              )}>
              {label}
            </label>
            {hasRequiredLabel && <span className="text-label-sm content-action-negative">*</span>}
          </div>
        )}

        {isSmall && !isTextarea ? (
          <div
            className={cn(
              "flex items-center gap-2 h-8 px-3 surface-base-fill input-box-shadow radius-xs",
              error && styles["input-error"],
              highlightClass,
              disabled && "cursor-not-allowed opacity-50",
            )}>
            {icon && <div className={cn("shrink-0", disabled && "pointer-events-none")}>{icon}</div>}
            <input
              id={inputId}
              ref={ref as React.ForwardedRef<HTMLInputElement>}
              disabled={disabled}
              autoComplete={autoCompleteValue}
              onKeyDown={handleNumberInput}
              className={cn(
                styles.input,
                styles["input-small"],
                "text-body-regular-sm dark:text-white flex-1 min-w-0",
                disabled && "cursor-not-allowed opacity-50",
                className,
              )}
              placeholder={placeholder}
              {...props}
              required={props.required}
            />
            {children}
          </div>
        ) : (
          <div className={cn("relative", isTextarea && "mt-auto")}>
            {icon && !isTextarea && (
              <div className={cn("absolute top-1/2 -translate-y-1/2 left-3", disabled && "pointer-events-none")}>
                {icon}
              </div>
            )}

            {isTextarea ? (
              <textarea
                id={inputId}
                ref={ref as React.ForwardedRef<HTMLTextAreaElement>}
                disabled={disabled}
                autoComplete={autoCompleteValue}
                className={cn(
                  styles.input,
                  "text-body-regular-sm dark:text-white input-box-shadow surface-base-fill radius-sm",
                  "h-[120px] resize-none p-3 component-scroll-thin",
                  highlightClass,
                  disabled && "cursor-not-allowed opacity-50",
                  className,
                )}
                placeholder={placeholder}
                onKeyDown={handleNumberInput}
                {...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
                required={props.required}
              />
            ) : (
              <input
                id={inputId}
                ref={ref as React.ForwardedRef<HTMLInputElement>}
                disabled={disabled}
                autoComplete={autoCompleteValue}
                onKeyDown={handleNumberInput}
                type={renderedType}
                className={cn(
                  styles.input,
                  "text-body-regular-sm dark:text-white input-box-shadow surface-base-fill radius-sm",
                  icon ? styles["input-icon"] : "",
                  isPasswordField ? styles["input-password"] : "",
                  error ? styles["input-error"] : "",
                  highlightClass,
                  disabled && "cursor-not-allowed opacity-50",
                  className,
                )}
                placeholder={placeholder}
                {...props}
                required={props.required}
              />
            )}

            {isPasswordField && !isTextarea && (
              <button
                type="button"
                className={cn(
                  "absolute top-1/2 -translate-y-1/2 right-3 flex items-center justify-center cursor-pointer",
                  "content-base-secondary hover:content-base-primary transition-colors",
                  disabled && "pointer-events-none opacity-50",
                )}
                aria-label={isPasswordVisible ? t("passwordVisibility.hide") : t("passwordVisibility.show")}
                aria-pressed={isPasswordVisible}
                aria-controls={inputId}
                onClick={() => setIsPasswordVisible((prev) => !prev)}
                tabIndex={disabled ? -1 : 0}
              >
                {isPasswordVisible ? (
                  <EyeSlash size={18} color="currentColor" />
                ) : (
                  <Eye size={18} color="currentColor" />
                )}
              </button>
            )}

            {children}
          </div>
        )}

        {error && !disabled && <p className="text-body-regular-xs content-action-negative">{error}</p>}
      </div>
    );
  },
);

Input.displayName = "Input";
